import type { Conversation, Message } from "../../types/messaging";

/**
 * Presentation helpers shared by the Chat screens. Kept framework-free (pure
 * functions) so they're easy to unit-test and reuse across the list, the
 * conversation header, and the message bubbles.
 */

/** Format a +1XXXXXXXXXX E.164 number as (XXX) XXX-XXXX; echo anything else. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  const digits = e164.replace(/[^\d]/g, "");
  const local =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return e164;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

/** Split a full name into { firstName, lastName } for StaffAvatar initials. */
export function splitName(name: string | null | undefined): {
  firstName: string;
  lastName?: string;
} {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" ") || undefined,
  };
}

/** The primary label for a conversation: the linked client's name, else phone. */
export function conversationDisplayName(conv: Conversation): string {
  return conv.client?.name || formatPhone(conv.client_phone_e164);
}

/** The secondary line: the phone for a linked client, else the unlinked hint. */
export function conversationSubtitle(conv: Conversation): string {
  return conv.client
    ? formatPhone(conv.client_phone_e164)
    : "Not linked to a client";
}

/** True when the conversation has no explicit client link. */
export function isUnlinked(conv: Conversation): boolean {
  return !conv.client;
}

/**
 * Hide the auto-added "[Business Name] " sender tag on outbound texts. Outbound
 * SMS are prefixed with the salon's name so recipients know who's texting; in the
 * admin thread that's just noise. Only a leading "[…] " tag whose bracketed text
 * matches a known business name (case-insensitive, whitespace-tolerant) is
 * stripped, so a bracket a staff member actually typed at the start of a message
 * is preserved. Direction is the caller's concern — pass only outbound bodies.
 */
export function stripOutboundBusinessPrefix(
  body: string | null,
  businessNames: (string | null | undefined)[],
): string | null {
  if (!body) return body;
  const match = body.match(/^\[([^\]]+)\]\s+/); // a leading "[...] " tag
  if (!match) return body;
  const tag = match[1].trim().toLowerCase();
  const known = businessNames
    .map((name) => (name || "").trim().toLowerCase())
    .filter(Boolean);
  return known.includes(tag) ? body.slice(match[0].length) : body;
}

const _STATUS_LABELS: Record<string, string> = {
  queued: "Sending",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
  undelivered: "Undelivered",
  canceled: "Canceled",
};

/** Human label for an outbound message's delivery state (empty for inbound). */
export function outboundStatusLabel(message: Message): string {
  if (message.direction !== "outbound") return "";
  return _STATUS_LABELS[message.status] || "";
}

/**
 * Coarse bucket that drives the message bubble's status icon + color. Empty for
 * inbound messages and unknown statuses (no indicator shown):
 * - `pending` — still leaving us (clock)
 * - `sent`    — accepted by / delivered to the carrier (check)
 * - `failed`  — a terminal non-delivery (alert, error color)
 */
export type OutboundStatusCategory = "pending" | "sent" | "failed" | "";

export function outboundStatusCategory(
  message: Message,
): OutboundStatusCategory {
  if (message.direction !== "outbound") return "";
  switch (message.status) {
    case "queued":
    case "sending":
      return "pending";
    case "sent":
    case "delivered":
      return "sent";
    case "failed":
    case "undelivered":
    case "canceled":
      return "failed";
    default:
      return "";
  }
}

/**
 * Outbound delivery lifecycle ranked so an out-of-order realtime event can't
 * regress a message to an earlier state (e.g. a stale `sent` arriving after
 * `delivered`). Higher = further along; the terminal states share the top rank
 * so whichever terminal state lands first "sticks". 0 = inbound / unknown.
 */
const _OUTBOUND_STATUS_RANK: Record<string, number> = {
  queued: 1,
  sending: 2,
  sent: 3,
  delivered: 4,
  undelivered: 4,
  failed: 4,
  canceled: 4,
};

export function outboundStatusRank(status: string | null | undefined): number {
  return (status && _OUTBOUND_STATUS_RANK[status]) || 0;
}

/**
 * Staff-friendly reason for the common Twilio delivery error codes. The realtime
 * payload is HIPAA-safe and carries only the raw code, which salon staff won't
 * recognize — so we translate the frequent ones to plain language and fall back
 * to the bare code for anything unmapped.
 */
const _TWILIO_ERROR_REASONS: Record<string, string> = {
  "21610": "Recipient unsubscribed",
  "21614": "Invalid mobile number",
  "30003": "Phone unreachable or off",
  "30004": "Message blocked by carrier",
  "30005": "Unknown or inactive number",
  "30006": "Landline or unreachable carrier",
  "30007": "Filtered as spam by carrier",
  "30008": "Carrier delivery error",
  "30034": "Number not registered for A2P messaging",
};

/** Human-readable failure detail for a message's `error_code` ('' when none). */
export function describeMessageError(
  errorCode: string | null | undefined,
): string {
  if (!errorCode) return "";
  const reason = _TWILIO_ERROR_REASONS[errorCode];
  return reason ? `${reason} (${errorCode})` : `Error ${errorCode}`;
}

/** The subset of a `message.status` realtime payload used to patch a message. */
export interface MessageStatusUpdate {
  message_uuid?: string;
  status?: string;
  error_code?: string | null;
  twilio_message_sid?: string | null;
}

/**
 * Apply a realtime `message.status` update to the open thread, matched by
 * `message_uuid`. Guards against out-of-order delivery callbacks: a status that
 * ranks at or below the message's current state is ignored, so a late `sent`
 * never regresses a `delivered` message. Returns the same array reference when
 * nothing changes, so React can skip the re-render.
 */
export function applyMessageStatus(
  messages: Message[],
  update: MessageStatusUpdate,
): Message[] {
  if (!update.message_uuid || !update.status) return messages;
  let changed = false;
  const next = messages.map((message) => {
    if (message.uuid !== update.message_uuid) return message;
    if (
      outboundStatusRank(update.status) <= outboundStatusRank(message.status)
    ) {
      return message; // stale / out-of-order event — don't regress
    }
    changed = true;
    return {
      ...message,
      status: update.status as Message["status"],
      error_code: update.error_code ?? message.error_code,
      twilio_message_sid:
        update.twilio_message_sid ?? message.twilio_message_sid,
    };
  });
  return changed ? next : messages;
}
